import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { BaseCRUDManager } from '../base/BaseCRUDManager';
import AlbumManager from '../AlbumManager';
import { PinManager } from '../PinManager';
import { isDemoMode } from '../../utilities/demoMode';
import { DemoDataManager } from '../../services/demoDataManager';
import { generateAlbumKey } from '../../utilities/albumKeyGenerator';
import { IPin } from '../../types/Pin';
import { IRecording } from '../../types/Recording';
import { getRecordingsForPin } from './queries';
import { getNextPinRecordingSequenceNumber } from '../../utils/pinRecordingSequence';
import { recordingLinkFieldsFromPin } from './pinRecordingLinks';

const RECORDINGS_COLLECTION = 'Recordings';

async function ensurePinAlbumKey(pin: IPin, recording: IRecording): Promise<string> {
  const existing = pin.albumKey?.trim();
  if (existing) return existing;

  const albumKey = await generateAlbumKey();
  const created = await AlbumManager.createAlbum({
    albumKey,
    name: pin.name?.trim() || pin.pinKey,
    projectKey: pin.project?.trim() || recording.project || null,
    userKey: recording.ownership?.recordingOwner ?? null,
  });
  const resolvedAlbumKey = created.albumKey || albumKey;
  await PinManager.updatePin({ ...pin, albumKey: resolvedAlbumKey });
  return resolvedAlbumKey;
}

/**
 * Assign a recording to a pin.
 * Copies the pin's album and project onto the recording so admin (pinKey)
 * and the app (pin albumKey) stay aligned.
 */
export async function assignRecordingToPin(
  recordingId: string,
  pinId: string
): Promise<void> {
  const demo = isDemoMode();
  const recording = demo
    ? DemoDataManager.getRecording(recordingId)
    : await (async () => {
        const recordingRef = doc(db, RECORDINGS_COLLECTION, recordingId);
        const recordingSnap = await getDoc(recordingRef);
        if (!recordingSnap.exists()) return null;
        return { ...(recordingSnap.data() as IRecording), key: recordingId };
      })();

  if (!recording) {
    throw new Error(`No recording found with ID: ${recordingId}`);
  }

  const pin = await PinManager.getPin(pinId);
  if (!pin) {
    throw new Error(`No pin found with ID: ${pinId}`);
  }

  const albumKey = await ensurePinAlbumKey(pin, recording);
  const linkFields = recordingLinkFieldsFromPin({
    ...pin,
    albumKey,
  });
  const alreadyOnPin = Boolean(recording.pinKey && recording.pinKey === pinId);
  const updates: Partial<IRecording> = {
    pinKey: pinId,
    ...linkFields,
  };

  if (!alreadyOnPin) {
    const pinRecordings = demo
      ? DemoDataManager.getRecordingsForPin(pinId)
      : await getRecordingsForPin(pinId);
    updates.sequenceNumber = getNextPinRecordingSequenceNumber(pinRecordings);
  }

  if (demo) {
    DemoDataManager.updateRecording(recordingId, updates);
    return;
  }

  await BaseCRUDManager.update<IRecording>(
    RECORDINGS_COLLECTION,
    recordingId,
    updates,
    { idField: 'key', autoTimestamps: false }
  );
}
